import {
  DashboardFilled,
  FileTextOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Link, useMatches, useNavigate } from '@tanstack/react-location';
import { Dropdown, Menu } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonOutline } from 'styled-icons/material-rounded';

import { refetchUser } from '@/hooks/useAuth';
import useUser from '@/hooks/useUser';
import SubscriptionMenuItem from '@/layout/SubscriptionMenuItem';
import { toAdminHome } from '@/views/AdminHome/routes';
import { toClientSettings } from '@/views/Client/routes';
import AddColleagueModal from '@/views/Client/Settings/MyColleagues/AddColleagueModal';
import { toSubscription } from '@/views/Subscription/routes';
import { toTermsOfService } from '@/views/TermsOfService/routes';
import { toUserSettings } from '@/views/Users/routes';

import { DropdownNav } from './index.styled';

export default function Account(): React.ReactElement {
  const { t } = useTranslation();
  const user = useUser();
  const [visible, setVisible] = useState(false);
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const matches = useMatches();

  const signOut = async (): Promise<void> => {
    localStorage.removeItem('auth-token');
    refetchUser(apolloClient);
  };

  const menuItems = [
    {
      key: toUserSettings(),
      icon: <UserOutlined />,
      label: <Link to={toUserSettings()}>{t('Account settings')}</Link>,
    },
    ...(user?.activeClientId
      ? [
          {
            key: toClientSettings(),
            icon: <SettingOutlined />,
            label: <Link to={toClientSettings()}>{t('Client settings')}</Link>,
          },
        ]
      : []),
    {
      key: toSubscription(),
      label: (
        <SubscriptionMenuItem
          key={toSubscription()}
          subscriptionType={user?.activeSubscription?.type}
        />
      ),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('Logout'),
      onClick: signOut,
    },
    ...(user?.activeClientId
      ? [
          {
            key: 'add-colleague',
            icon: <UserAddOutlined />,
            label: t('Add colleague'),
            onClick: () => setVisible(true),
          },
        ]
      : []),
    {
      key: toTermsOfService(),
      icon: <FileTextOutlined />,
      label: <Link to={toTermsOfService()}>{t('Terms of service')}</Link>,
    },
  ];

  if (user?.role === 'admin' || user?.role === 'consultant') {
    menuItems.push({
      key: toAdminHome(),
      icon: <DashboardFilled />,
      label: <Link to={toAdminHome()}>{t('Admin Panel')}</Link>,
    });
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="topLeft"
        arrow
        align={{
          offset: [20, 11],
        }}
      >
        <button
          type="button"
          style={{
            fontSize: '11px',
            color: '#f0f0f0',
            padding: '10px 0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            width: '100%',
            background: 'transparent',
            border: 'none',
          }}
          className="dropdown tour tour-settings"
        >
          <PersonOutline
            style={{ width: '26px', height: '26px', marginBottom: '5px' }}
          />
          <span className="text" style={{ fontSize: '11px' }}>
            {t('Account')}
          </span>
        </button>
      </Dropdown>
      <AddColleagueModal visible={visible} setVisible={setVisible} />
    </>
  );
}
